/**
 * TASK-ATTR-013: Effect Layer Manager
 *
 * High-level manager for combat attribute effect rendering.
 * Provides a simple API for triggering effects during combat.
 */

import * as PIXI from 'pixi.js';
import { EffectSpriteFactory } from './effect-sprite-factory';
import { EffectSpritePool } from './effect-sprite-pool';
import { EffectPlaybackSystem, EffectPlaybackOptions } from './effect-playback-system';

/**
 * High-level manager for combat attribute effects
 * Simplifies effect triggering and lifecycle management
 */
export class EffectLayerManager {
  private factory: EffectSpriteFactory;
  private pool: EffectSpritePool;
  private playbackSystem: EffectPlaybackSystem;
  private container: PIXI.Container;
  private initialized = false;

  constructor(parentContainer: PIXI.Container) {
    // Create effect container layer
    this.container = new PIXI.Container();
    this.container.name = 'effectLayer';
    parentContainer.addChild(this.container);

    // Create factory and pool
    this.factory = new EffectSpriteFactory();
    this.pool = new EffectSpritePool(this.factory);
    this.playbackSystem = new EffectPlaybackSystem(this.pool, this.container);
  }

  /**
   * Initialize the effect layer manager
   * Can be used for async setup if needed
   */
  initialize(): void {
    if (this.initialized) {
      console.warn('[EffectLayerManager] Already initialized');
      return;
    }

    this.initialized = true;
    console.log('[EffectLayerManager] Initialized');
  }

  /**
   * Pre-load effect frames for a unit's visual attributes
   * Call this during texture preloading phase to ensure effects render properly
   *
   * @param attributes - Array of visual attributes with effectFrames
   */
  async preloadEffectFrames(attributes: Array<{ effectFrames?: string[] }>): Promise<void> {
    if (!attributes || attributes.length === 0) {
      return;
    }

    const allFrames: string[] = [];
    for (const attr of attributes) {
      if (attr.effectFrames && attr.effectFrames.length > 0) {
        allFrames.push(...attr.effectFrames);
      }
    }

    if (allFrames.length > 0) {
      console.log(`[EffectLayerManager] Preloading ${allFrames.length} effect frames...`);
      await this.pool.preloadFrames(allFrames);
      console.log(`[EffectLayerManager] ✓ Preloaded ${allFrames.length} effect frames`);
    }
  }

  /**
   * Get the underlying factory (for direct texture preloading)
   */
  getFactory(): EffectSpriteFactory {
    return this.factory;
  }

  /**
   * Trigger an effect at a specific position
   *
   * @param attributeId - Combat attribute ID
   * @param frames - Array of base64-encoded PNG frames
   * @param position - Screen position to play effect at
   * @param options - Additional playback options
   * @returns Unique effect ID for tracking
   */
  triggerEffect(
    attributeId: string,
    frames: string[],
    position: { x: number; y: number },
    options?: Partial<EffectPlaybackOptions>
  ): string {
    if (!this.initialized) {
      console.warn('[EffectLayerManager] Not initialized - initializing now');
      this.initialize();
    }

    // Generate unique effect ID
    const effectId = `${attributeId}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    // Merge options with defaults
    const playbackOptions: EffectPlaybackOptions = {
      position,
      rotation: options?.rotation ?? 0,
      scale: options?.scale ?? 1.0,
      blendMode: options?.blendMode ?? 'normal',
      onComplete: options?.onComplete,
      loop: options?.loop ?? false
    };

    // Play the effect
    this.playbackSystem.play(effectId, frames, playbackOptions);

    console.log(`[EffectLayerManager] Triggered effect: ${effectId} at (${position.x}, ${position.y})`);

    return effectId;
  }

  /**
   * Update the effect layer (called every frame)
   *
   * @param delta - Time delta in seconds
   */
  update(delta: number): void {
    if (!this.initialized) {
      return;
    }

    // Update playback system
    this.playbackSystem.update(delta);
  }

  /**
   * Stop a specific effect
   *
   * @param effectId - Effect ID to stop
   */
  stopEffect(effectId: string): void {
    this.playbackSystem.stop(effectId);
  }

  /**
   * Stop all active effects
   */
  stopAllEffects(): void {
    this.playbackSystem.stopAll();
  }

  /**
   * Get statistics about the effect system
   */
  getStats() {
    return {
      initialized: this.initialized,
      ...this.playbackSystem.getStats(),
      factoryStats: this.factory.getCacheStats()
    };
  }

  /**
   * Dispose of the effect layer manager
   * Cleans up all resources
   */
  dispose(): void {
    if (!this.initialized) {
      return;
    }

    console.log('[EffectLayerManager] Disposing...');

    // Stop all effects
    this.playbackSystem.stopAll();

    // Destroy playback system
    this.playbackSystem.destroy();

    // Destroy pool
    this.pool.destroy();

    // Clear factory cache
    this.factory.clearCache();

    // Remove container from parent
    if (this.container.parent) {
      this.container.parent.removeChild(this.container);
    }

    // Destroy container
    this.container.destroy({ children: true });

    this.initialized = false;
    console.log('[EffectLayerManager] Disposed');
  }
}
